import mixpanel from 'mixpanel-browser';
import { TrackingConfig, TrackOptions } from './telemetry.contracts';

export class Telemetry {
	public enabled = false;
	public readonly version = 2;

	constructor(private readonly config: TrackingConfig) {
		if (config.token) {
			try {
				mixpanel.init(config.token, {
					persistence: 'localStorage',
					track_pageview: false,
					api_host: 'https://pa.cloud.matrix42.com',
				});
				this.enabled = true;
			} catch (e) {
				this.enabled = false;
			}
		}
	}

	public identify(userId: string) {
		const $user_id = mixpanel.get_property('$user_id');

		if (userId !== $user_id) {
			if ($user_id) {
				this.reset();
			}
			mixpanel.people.set(userId, {});
			mixpanel.identify(userId);
		}
	}

	public reset() {
		mixpanel.reset();
	}

	track(trackOptions: TrackOptions) {
		if (!this.enabled) {
			return;
		}
		try {
			const { sender, senderTitle, contextId, contextTypeId, contextTypeTitle, applicationTitle, applicationId, navigationTitle, navigationId, eventName, ...others } =
				trackOptions;
			const props = {
				...others,
				mx42_uux_sender_element_id: sender || null,
				mx42_uux_sender_element: senderTitle || null,

				mx42_uux_context_id: contextId || null,

				mx42_uux_context_type_id: contextTypeId || null,
				mx42_uux_context_type: contextTypeTitle || null,

				mx42_uux_application_id: applicationId || null,
				mx42_uux_application: applicationTitle || null,

				mx42_uux_navigation_id: navigationId || null,
				mx42_uux_navigation: navigationTitle || null,

				mx42_uux_language: this.config.language || null,
				mx42_uux_mode: document.cookie.includes(`NewUI=1`) ? 'New Look' : 'Classic Look',
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				mx42_uux_contrast_mode: (window.mx as any)?.isEnhancedColorContrastMode ? '1' : '0',

				mx42_ui_scale_factor: window.devicePixelRatio,

				mx42_customer_id: this.config.customerId || null,
				mx42_customer: this.config.customerName || null,
				mx42_source: 'UUX',
				mx42_product: 'ESM',
				mx42_deployment_type: location.hostname.endsWith('m42cloud.com') ? 'SaaS' : location.hostname.endsWith('m42cloud.biz') ? 'SaaS Dev' : 'OnPrem',
				mx42_product_version: this.config.productVersion || null,
				mx42_database_id: this.config.envId?.toLowerCase() || null,
				mx42_environment_type: this.config.envType || null,
				mx42_license_id: this.config.licenseId?.toLowerCase() || null,
				mx42_device_type: this.getDeviceType(),

				mx42_user_roles: this.config.roles || null,

				mx42_site_id: null,

				m42_telemetry_version: this.version,

				$referrer: null,
				$referring_domain: null,
				$current_url: location.pathname,
				$ip: null,
			};

			if (this.config.debugTelemetry) {
				Object.assign(props, {
					$host_temp: location.origin,
				});
			}

			mixpanel.track(eventName, props);
		} catch (e) {
			if (this.config.debugTelemetry) {
				console.error(e);
			}
			this.enabled = false;
		}
	}

	private getDeviceType() {
		const screenWidth = window.screen.width;
		const isMobileDevice = screenWidth < 960 && 'ontouchstart' in document;
		const screenExtraSmall = screenWidth < 600;
		return isMobileDevice ? (screenExtraSmall ? 'Mobile' : 'Tablet') : 'Desktop';
	}

	public updateConfig(config: Partial<TrackingConfig>) {
		Object.assign(this.config, config);
	}
}
